/**
 * Custom hook to handle recording initialization
 */
import { useEffect, useRef } from 'react';
import EgressHelper from '@livekit/egress-sdk';
import { Track } from 'livekit-client';
import { useLogger } from '@ui/shared/components/Logger';
import { LOGGER_LABEL } from '../utils/constants';

const FRAME_DECODE_TIMEOUT = 5000;
const MAX_START_ATTEMPTS = 100; // 10 seconds at 100ms intervals
const CHECK_INTERVAL = 100;

/**
 * Hook to determine when to start recording based on track availability
 *
 * @param {object} room - LiveKit room instance
 */
export const useRecordingStart = (room) => {
  const logger = useLogger();

  useEffect(() => {
    if (!room) {
      logger.error(LOGGER_LABEL, 'Room is not available');
      return;
    }

    logger.info(LOGGER_LABEL, 'Initializing recording start logic', {
      roomName: room.name,
      roomState: room.state,
      participantCount: room.remoteParticipants.size
    });

    try {
      // Set up the room for recording
      EgressHelper.setRoom(room);
      logger.debug(LOGGER_LABEL, 'Room set in EgressHelper');
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Failed to set room in EgressHelper', {
        error,
        errorMessage: error.message
      });
      return;
    }

    // determines when to start recording
    // the algorithm used is:
    // * if there are video tracks published, wait for frames to be decoded
    // * if there are no video tracks published, start immediately
    // * if it's been more than 10s, record as long as there are tracks subscribed
    const startTime = Date.now();
    const attemptCountRef = { current: 0 };
    let intervalId = null;

    const checkAndStartRecording = async () => {
      attemptCountRef.current++;

      try {
        let shouldStartRecording = false;
        let hasVideoTracks = false;
        let hasSubscribedTracks = false;
        let hasDecodedFrames = false;

        const participants = Array.from(room.remoteParticipants.values());

        for (const p of participants) {
          for (const pub of Array.from(p.trackPublications.values())) {
            if (pub.isSubscribed) {
              hasSubscribedTracks = true;
            }
            if (pub.kind === Track.Kind.Video) {
              hasVideoTracks = true;
              if (pub.videoTrack) {
                try {
                  const stats = await pub.videoTrack.getRTCStatsReport();
                  if (stats) {
                    hasDecodedFrames = Array.from(stats).some(
                      (item) =>
                        item[1].type === 'inbound-rtp' && item[1].framesDecoded > 0
                    );
                    
                    if (hasDecodedFrames) {
                      logger.debug(LOGGER_LABEL, 'Found decoded frames', {
                        participantIdentity: p.identity,
                        trackSid: pub.trackSid
                      });
                    }
                  }
                } catch (error) {
                  logger.warn(LOGGER_LABEL, 'Failed to get RTC stats for track', {
                    error,
                    participantIdentity: p.identity,
                    trackSid: pub.trackSid
                  });
                }
              }
            }
          }
        }

        const timeDelta = Date.now() - startTime;

        // Log every 10th attempt to avoid log spam
        if (attemptCountRef.current % 10 === 0) {
          logger.debug(LOGGER_LABEL, 'Checking recording start conditions', {
            attemptCount: attemptCountRef.current,
            timeDelta,
            hasVideoTracks,
            hasSubscribedTracks,
            hasDecodedFrames,
            participantCount: participants.length
          });
        }

        // Determine if should start recording
        if (hasDecodedFrames) {
          shouldStartRecording = true;
          logger.info(LOGGER_LABEL, 'Starting recording: decoded frames detected', {
            attemptCount: attemptCountRef.current,
            timeDelta
          });
        } else if (!hasVideoTracks && hasSubscribedTracks && timeDelta > 500) {
          // adding a small timeout to ensure video tracks has a chance to be published
          shouldStartRecording = true;
          logger.info(LOGGER_LABEL, 'Starting recording: audio-only session', {
            attemptCount: attemptCountRef.current,
            timeDelta
          });
        } else if (timeDelta > FRAME_DECODE_TIMEOUT && hasSubscribedTracks) {
          shouldStartRecording = true;
          logger.warn(LOGGER_LABEL, 'Starting recording: timeout reached', {
            attemptCount: attemptCountRef.current,
            timeDelta,
            hasVideoTracks,
            hasSubscribedTracks
          });
        }

        // Check max attempts
        if (attemptCountRef.current >= MAX_START_ATTEMPTS && !shouldStartRecording) {
          logger.error(LOGGER_LABEL, 'Max attempts reached without starting recording', {
            attemptCount: attemptCountRef.current,
            timeDelta,
            hasVideoTracks,
            hasSubscribedTracks,
            hasDecodedFrames
          });
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          return;
        }

        if (shouldStartRecording) {
          try {
            EgressHelper.startRecording();
            logger.info(LOGGER_LABEL, 'Recording started successfully', {
              attemptCount: attemptCountRef.current,
              timeDelta,
              hasVideoTracks,
              hasDecodedFrames
            });
            
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          } catch (error) {
            logger.error(LOGGER_LABEL, 'Failed to start recording', {
              error,
              errorMessage: error.message,
              attemptCount: attemptCountRef.current,
              timeDelta
            });
            
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          }
        }
      } catch (error) {
        logger.error(LOGGER_LABEL, 'Error in recording start check', {
          error,
          attemptCount: attemptCountRef.current,
          timeSinceStart: Date.now() - startTime
        });
      }
    };

    intervalId = setInterval(checkAndStartRecording, CHECK_INTERVAL);

    // Clean up interval on unmount
    return () => {
      if (intervalId) {
        logger.debug(LOGGER_LABEL, 'Cleaning up recording start interval', {
          attemptCount: attemptCountRef.current,
          timeSinceStart: Date.now() - startTime
        });
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [room, logger]);
};
